import { useQuery } from "@tanstack/react-query";
import { agentDashboardsApi } from "../../api/agentDashboards";
import { usePractice } from "../../context/PracticeContext";
import { queryKeys } from "../../lib/queryKeys";
import { WidgetChart } from "./WidgetChart";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AgentDashboardWidget } from "@ligament/shared";

interface Props {
  widget: AgentDashboardWidget;
}

export function DashboardWidgetCard({ widget }: Props) {
  const { selectedPracticeId } = usePractice();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.agentDashboards.widgetData(widget.id),
    queryFn: () => agentDashboardsApi.runQuery(selectedPracticeId!, widget.lookerQuery),
    enabled: !!selectedPracticeId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });

  return (
    <div className="border border-border rounded-lg p-4 flex flex-col h-full bg-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-medium text-muted-foreground truncate">
          {widget.title}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="shrink-0 h-6 w-6 p-0"
        >
          <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="flex-1 min-h-[240px]">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-muted-foreground animate-pulse">
              Loading data...
            </p>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-destructive">
              {error instanceof Error ? error.message : "Failed to load data"}
            </p>
          </div>
        )}
        {data && data.error && (
          <div className="flex items-center justify-center h-full px-2">
            <p className="text-xs text-destructive text-center">{data.error}</p>
          </div>
        )}
        {data && !data.error && (
          <WidgetChart
            vizType={widget.vizType}
            data={data}
            config={widget.config}
          />
        )}
      </div>
    </div>
  );
}
